import { Component } from '@theme/component';

/**
 * Highlight Showcase
 *
 * A split panel where a set of "highlights" (title + description + image) share
 * a single main image and description area. Selecting a highlight — by hovering
 * its title on desktop, or tapping a thumbnail on any device — swaps the active
 * main image, description and title styling.
 *
 * All selectable controls carry `data-hs-index`; the matching image / description
 * / title elements carry the same index. Selection just toggles `.is-active`.
 */
class HighlightShowcase extends Component {
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    // Sync the initial active state (defaults to the first highlight).
    this.select(this.#activeIndex);
  }

  /**
   * Activate a highlight by index.
   * Called via `on:click` / `on:pointerenter` declarative bindings, which pass
   * the parsed index as the first argument.
   * @param {number} index
   */
  select(index) {
    if (typeof index !== 'number' || Number.isNaN(index)) return;

    this.#activeIndex = index;
    const active = String(index);

    for (const el of this.querySelectorAll('[data-hs-index]')) {
      el.classList.toggle('is-active', el.dataset.hsIndex === active);
    }

    this.#revealActiveThumb();
  }

  /**
   * Keeps the active thumbnail inside the strip's visible window.
   *
   * The strip scrolls internally once the thumbs outrun the main image's
   * height, and selection can come from a title hover, so the matching thumb
   * may be out of view. Scrolls the strip itself rather than using
   * `scrollIntoView`, which would also scroll the page.
   */
  #revealActiveThumb() {
    const thumb = this.querySelector('.highlight-showcase__thumb.is-active');
    const strip = thumb?.closest('.highlight-showcase__thumbs');
    if (!thumb || !strip || strip.scrollHeight <= strip.clientHeight) return;

    const top = strip.scrollTop + (thumb.getBoundingClientRect().top - strip.getBoundingClientRect().top);
    const bottom = top + thumb.offsetHeight;

    if (top < strip.scrollTop) {
      strip.scrollTo({ top, behavior: 'smooth' });
    } else if (bottom > strip.scrollTop + strip.clientHeight) {
      strip.scrollTo({ top: bottom - strip.clientHeight, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('highlight-showcase')) {
  customElements.define('highlight-showcase', HighlightShowcase);
}
